import { useEffect, useState } from 'react'
import { fieldLabel, fieldWrapper, inputBase } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { api } from '@/lib/axios'
import { useTerminology } from '@/lib/useTerminology'
import { createPosition } from '../api/projects-phase-d.api'
import type { PositionDetail } from '../types/phase-d.types'
import type { ProjectDetail } from '../types/project.types'

interface Einheit {
  id: string
  code: string
  name: string
  decimals: number
}

/**
 * AP-35 Detailblatt 2 7.1 — **„+ Position" von Hand.**
 *
 * Der zweite und letzte Weg, auf dem eine Position entsteht (der erste ist das
 * Einfügen aus der Zwischenablage). Nummer, Reihenfolge und Anfangsstatus
 * vergibt der Server — die Maske schickt nur, was ein Mensch eingegeben hat.
 *
 * Seit AP-36 F (4.2) steht hier nur noch, was zum Anlegen nötig ist: Bezeichnung,
 * Menge, Einheit — beim Nachtrag dazu sein Anlass. Langtext und die
 * Kunden-Auftragsdaten des Sammelauftrags stehen danach im Reiter „Details" und
 * speichern dort feldweise.
 */
export function PositionFormDialog({
  projekt,
  art,
  onClose,
  onSaved,
}: {
  projekt: ProjectDetail
  art: 'SERVICE' | 'SUPPLEMENT'
  onClose: () => void
  onSaved: (position: PositionDetail) => void
}) {
  const terms = useTerminology()
  const [name, setName] = useState('')
  // KA-49: keine vorgeschlagene Menge — leer gilt 1 (Platzhalter).
  const [menge, setMenge] = useState('')
  const [unitId, setUnitId] = useState('')
  const [supplementNote, setSupplementNote] = useState('')
  const [einheiten, setEinheiten] = useState<Einheit[]>([])
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    // Die Einheit ist ein Verweis auf `ArticleUnit`, kein Freitext (14.2-14) —
    // sie bestimmt die erlaubten Nachkommastellen der Menge.
    void api
      .get<{ data: Einheit[] }>('/projects/lookups/units')
      .then((res) => setEinheiten(res.data.data ?? []))
      .catch(() => setEinheiten([]))
  }, [])

  async function anlegen() {
    setLaeuft(true)
    setFehler(null)
    try {
      const neu = await createPosition(projekt.id, {
        name: name.trim(),
        type: art,
        quantity: menge.trim() ? Number(menge.replace(',', '.')) : 1,
        unitId: unitId || undefined,
        supplementNote: art === 'SUPPLEMENT' ? supplementNote.trim() || undefined : undefined,
      })
      onSaved(neu)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg space-y-4 rounded-lg border border-border bg-background p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-primary">
          {art === 'SUPPLEMENT' ? 'Nachtrag anlegen' : `${terms.position} anlegen`}
        </h2>

        <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
          <div className={fieldWrapper}>
            <label className={fieldLabel} htmlFor="pos-name">
              Bezeichnung
            </label>
            <input
              id="pos-name"
              className={inputBase}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              autoFocus
            />
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel} htmlFor="pos-menge">
              Menge
            </label>
            <input
              id="pos-menge"
              className={inputBase}
              value={menge}
              onChange={(e) => setMenge(e.target.value)}
              inputMode="decimal"
              placeholder="1"
            />
          </div>
        </div>

        <div className={fieldWrapper}>
          <label className={fieldLabel} htmlFor="pos-einheit">
            Einheit
          </label>
          <select
            id="pos-einheit"
            className={inputBase}
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
          >
            <option value="">Vorgabe des Mandanten</option>
            {einheiten.map((e) => (
              <option key={e.id} value={e.id}>
                {e.code} — {e.name}
              </option>
            ))}
          </select>
        </div>

        {art === 'SUPPLEMENT' && (
          <div className={fieldWrapper}>
            <label className={fieldLabel} htmlFor="pos-anlass">
              Anlass des Nachtrags
            </label>
            <input
              id="pos-anlass"
              className={inputBase}
              value={supplementNote}
              onChange={(e) => setSupplementNote(e.target.value)}
              maxLength={200}
            />
          </div>
        )}

        {fehler && <p className="text-sm text-destructive">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            disabled={laeuft || !name.trim()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
            onClick={anlegen}
          >
            Anlegen
          </button>
        </div>
      </div>
    </div>
  )
}
